import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PricingCard from './PricingCard';
import { axiosInstanceV2 } from '../../../services/axios';
import '../subscription-plan.scss';
import useSubscription from '../../../hooks/useSubscription';
import useSubUrl from '../../../hooks/useSubUrl';

const PricingSection = ({ billing, setBilling, plans = [], hideFreePlan = false }) => {
	const navigate = useNavigate();
	const { subscription } = useSubscription();
	const isExistingUser = Boolean(subscription);
	const { suburl: suburlParam } = useParams();
	const suburl = useSubUrl();
	const activeSuburl = suburlParam || suburl;
	const [showModal, setShowModal] = useState(false);
	const [loading, setLoading] = useState(false);

	// Filter plans
	const filteredPlans = useMemo(() => {
		return plans
			.filter((plan) => !hideFreePlan || plan.key !== 'free')
			.filter((plan) => {
				if (plan.key === 'free') return true;

				return plan.billing?.some(({ period }) => period === billing);
			});
	}, [plans, hideFreePlan, billing]);

	// Free Plan Modal
	const handleOpenFreeModal = () => {
		setShowModal(true);
	};

	const activateFreePlan = async () => {
		try {
			setLoading(true);
			const authToken = localStorage.getItem('authToken');
			const response = await axiosInstanceV2.get('/subscription/start-free', {
				headers: {
					Authorization: `Bearer ${authToken}`,
				},
			});

			if (response?.data?.success) {
				toast.success('Your Free Plan is Active');
				setShowModal(false);
				navigate('/organizationDetail');
				return;
			}

			if (response?.data?.message === 'Subscription already exists') {
				toast.info('Free plan already active.');
				setShowModal(false);
				navigate('/organizationDetail');
				return;
			}
			toast.error(response?.data?.message ?? 'Failed to activate free plan');
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Failed to activate free plan');
		} finally {
			setLoading(false);
		}
	};

	// Plan Click
	const handleSelectPlan = (plan) => {
		if (!isExistingUser) {
			const checkoutPath = activeSuburl ? `/user/${activeSuburl}/checkout` : '/checkout';
			navigate(checkoutPath, {
				state: {
					plan,
					billing,
				},
			});
		}
	};

	return (
		<>
			<div className="pricing-wrapper">
				{/* Billing Toggle */}
				<div className="billing-toggle">
					<button
						className={billing === 'monthly' ? 'active' : ''}
						onClick={() => setBilling('monthly')}
					>
						Monthly
					</button>
					<button
						className={billing === 'yearly' ? 'active' : ''}
						onClick={() => setBilling('yearly')}
					>
						Yearly
					</button>
				</div>

				{/* Cards */}

				<div className="pricing-grid">
					{!filteredPlans.length && (
						<div className="w-100 text-center py-5">No {billing} plans available.</div>
					)}

					{filteredPlans.map((plan) => {
						const billingDetails =
							plan.key === 'free'
								? plan.billing?.[0] || null
								: plan.billing.find((item) => item.period === billing);

						return (
							<PricingCard
								key={plan._id || plan.key}
								plan={plan}
								billing={billing}
								billingDetails={billingDetails}
								currentPlanKey={null}
								currentRazorpayPlanId={null}
								// Was never passed here, so PricingCard's isCurrentPlan
								// fallback (changeType === 'Current Plan') always compared
								// against undefined — the Free card could never show as
								// current on this page even after the backend started
								// sending is_current/change_type correctly for it, since
								// Free's billingDetails is always null (no billing entries)
								// and there was no changeType to fall back to instead.
								changeType={billingDetails?.change_type ?? plan.change_type}
								onOpenFreeModal={handleOpenFreeModal}
								onSelectPlan={handleSelectPlan}
							/>
						);
					})}
				</div>
			</div>

			{/* Free Plan Modal */}
			{showModal && (
				<div
					className="modal fade show"
					style={{
						display: 'block',
						background: 'rgba(0,0,0,.5)',
					}}
				>
					<div className="modal-dialog modal-dialog-top">
						<div className="modal-content">
							<div className="modal-header">
								<h5>Free Plan Activation</h5>

								<button
									className="btn-close"
									onClick={() => setShowModal(false)}
								/>
							</div>

							<div className="modal-body">
								<p className="fw-semibold">Your Free Plan is Ready 🎉</p>

								<p className="text-muted">
									Continue with the free plan to complete your organization setup.
								</p>
							</div>

							<div className="modal-footer">
								<button
									className="btn-cancel"
									onClick={() => setShowModal(false)}
								>
									Cancel
								</button>

								<button
									disabled={loading}
									className="btn-primary-modal"
									onClick={activateFreePlan}
								>
									{loading ? 'Loading...' : 'Continue Setup'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default PricingSection;
